import React from 'react';
import {
  ApplicationIcon,
  Box,
  ButtonIcon,
  Chip,
  Icon,
  Loader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import type { User } from '../../../../services';
import type { InvertedLocalize } from '../../../../utils/localization';
import type { EntitlementRow } from '../IncludedEntitlements';
import type { IncludedEntitlementsMessage } from '../messages';

import {
  getScoreChipClassName,
  getScoreChipColor,
  getScoreIcon,
} from './utilityFunctions';

// Type for localization function
type LocalizeFn = InvertedLocalize<IncludedEntitlementsMessage>;

// Helper function to convert boolean accessStatus to string
const getAccessStatusText = (l: LocalizeFn, accessStatus?: boolean): string => {
  if (accessStatus === true) {
    return l('Present');
  }

  if (accessStatus === false) {
    return l('Absent');
  }

  return '-'; // default to dash to help identify any data issues
};

/**
 * Creates the center content for parent rows (entitlement rows)
 */
export const createParentCenterContent = (
  entitlement: EntitlementRow,
  onRowClick?: (data: EntitlementRow) => void
) => (
  // click handler added due to increased z-index which blocks the sidepanel from opening
  <div
    className='Rules-IncludedEntitlements-centerContent'
    onClick={() => {
      onRowClick?.(entitlement);
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRowClick?.(entitlement);
      }
    }}
    role='button'
    tabIndex={0}>
    <Typography
      kind='body1-bold'
      lineLimit={1}
      tooltipConfigs={{
        text: entitlement.evDisplayName ?? entitlement.entitlementValueName,
        placement: 'top',
        maxWidth: '10rem',
      }}
      className='Rules-IncludedEntitlements-endpointName'>
      {entitlement.evDisplayName ?? entitlement.entitlementValueName}
    </Typography>
    <Typography
      kind='body2'
      lineLimit={1}
      tooltipConfigs={{
        text: entitlement.evDescription ?? '',
        placement: 'top',
        maxWidth: '10rem',
      }}
      className='Rules-IncludedEntitlements-endpointDescription'>
      {entitlement.evDescription}
    </Typography>
  </div>
);

/**
 * Creates the trailing content for parent rows (entitlement rows)
 */
export const createParentTrailingContent = (
  entitlement: EntitlementRow,
  totalVisibleEntRows: number,
  onDeleteClick: (entitlement: EntitlementRow) => void,
  l: LocalizeFn
) => {
  const scoreIcon = getScoreIcon(entitlement.trustScore);
  const chipColor = getScoreChipColor(entitlement.trustScore);
  const chipClassName = getScoreChipClassName(entitlement.trustScore);
  const isLastEntitlement = totalVisibleEntRows === 1;
  const usersTooltipText = l(
    "{totalUsers} users meet this rule's criteria. {presentUserCount} currently have access to the entitlement.",
    {
      totalUsers: entitlement.totalUserCount,
      presentUserCount: entitlement.existingUserCount, // use presentUserCount here so we don't break the translation key
    }
  );

  return (
    <Box className='Rules-IncludedEntitlements-trailingContent'>
      <ApplicationIcon
        logo={entitlement.logo}
        size='small'
        className='Rules-IncludedEntitlements-systemIcon'
      />

      <Chip
        size='small'
        label={entitlement.trustScore.toString()}
        kind='outlined'
        color={chipColor}
        leftIcon={<Icon kind={scoreIcon} />}
        className={cx('Rules-IncludedEntitlements-scoreChip', chipClassName)}
        isInteractionDisabled
      />

      <Box className='Rules-IncludedEntitlements-userMetric'>
        <Icon kind='User' size='small' />
        <Tooltip placement='top' text={usersTooltipText}>
          <Typography
            kind='body2'
            className='Rules-IncludedEntitlements-userCount'>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {entitlement.existingUserCount}/{entitlement.totalUserCount}
          </Typography>
        </Tooltip>
      </Box>

      <ButtonIcon
        isDisabled={isLastEntitlement}
        size='medium'
        kind='ghost'
        icon={<Icon kind='Delete' />}
        ariaLabel={
          isLastEntitlement
            ? l('Cannot delete the last entitlement')
            : l('Delete entitlement')
        }
        className='Rules-IncludedEntitlements-deleteButton'
        onClick={
          isLastEntitlement ? undefined : () => onDeleteClick(entitlement)
        }
      />
    </Box>
  );
};

/**
 * Creates the center content for user rows
 */
export const createUserCenterContent = (
  user: User,
  l: LocalizeFn,
  onRowClick?: (user: User) => void
) => (
  <div
    className='Rules-IncludedEntitlements-userRowContent'
    onClick={() => {
      onRowClick?.(user);
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRowClick?.(user);
      }
    }}
    role='button'
    tabIndex={0}>
    {/* User info section */}
    <Box className='Rules-IncludedEntitlements-userColumn'>
      <Box className='Rules-IncludedEntitlements-userInfo'>
        <Icon
          kind='User'
          size='medium'
          className='Rules-IncludedEntitlements-userIcon'
        />
        <Box className='Rules-IncludedEntitlements-userDetails'>
          <Typography
            kind='body3'
            className='Rules-IncludedEntitlements-userLabel'>
            {l('User')}
          </Typography>
          <Typography
            kind='body2-bold'
            lineLimit={1}
            tooltipConfigs={{
              text: user.username,
              placement: 'top',
              maxWidth: '10rem',
            }}
            className='Rules-IncludedEntitlements-userName'>
            {/* Show available name parts or dash if both are null/undefined */}
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {[user.firstname, user.lastname].filter(Boolean).join(' ') || '-'}
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* Username column */}
    <Box className='Rules-IncludedEntitlements-userDataColumn'>
      <Typography
        kind='body3'
        lineLimit={1}
        tooltipConfigs={{
          text: l('Username'),
          placement: 'top',
          maxWidth: '10rem',
        }}
        className='Rules-IncludedEntitlements-columnLabel'>
        {l('Username')}
      </Typography>
      <Typography
        kind='body2'
        className='Rules-IncludedEntitlements-columnValue'
        lineLimit={1}
        tooltipConfigs={{
          text: user.username,
          placement: 'top',
          maxWidth: '10rem',
        }}>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {user.username ?? '-'}
      </Typography>
    </Box>

    {/* Job Title column */}
    <Box className='Rules-IncludedEntitlements-userDataColumn'>
      <Typography
        kind='body3'
        lineLimit={1}
        tooltipConfigs={{
          text: l('Job Title'),
          placement: 'top',
          maxWidth: '10rem',
        }}
        className='Rules-IncludedEntitlements-columnLabel'>
        {l('Job Title')}
      </Typography>
      <Typography
        kind='body2'
        className='Rules-IncludedEntitlements-columnValue'
        lineLimit={1}
        tooltipConfigs={{
          text: user.title,
          placement: 'top',
          maxWidth: '10rem',
        }}>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {user.title || '-'}
      </Typography>
    </Box>

    {/* Department column */}
    <Box className='Rules-IncludedEntitlements-userDataColumn'>
      <Typography
        kind='body3'
        lineLimit={1}
        tooltipConfigs={{
          text: l('Department'),
          placement: 'top',
          maxWidth: '10rem',
        }}
        className='Rules-IncludedEntitlements-columnLabel'>
        {l('Department')}
      </Typography>
      <Typography
        kind='body2'
        className='Rules-IncludedEntitlements-columnValue'
        lineLimit={1}
        tooltipConfigs={{
          text: user.departmentname,
          placement: 'top',
          maxWidth: '10rem',
        }}>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {user.departmentname ?? '-'}
      </Typography>
    </Box>

    {/* Manager column */}
    <Box className='Rules-IncludedEntitlements-userDataColumn'>
      <Typography
        kind='body3'
        lineLimit={1}
        tooltipConfigs={{
          text: l('Manager'),
          placement: 'top',
          maxWidth: '10rem',
        }}
        className='Rules-IncludedEntitlements-columnLabel'>
        {l('Manager')}
      </Typography>
      <Typography
        kind='body2'
        className='Rules-IncludedEntitlements-columnValue'
        lineLimit={1}
        tooltipConfigs={{
          text: user.manager,
          placement: 'top',
          maxWidth: '10rem',
        }}>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {user.manager || '-'}
      </Typography>
    </Box>

    {/* Access Status column */}
    <Box className='Rules-IncludedEntitlements-userDataColumn'>
      <Typography
        kind='body3'
        lineLimit={1}
        tooltipConfigs={{
          text: l('Access Status'),
          placement: 'top',
          maxWidth: '10rem',
        }}
        className='Rules-IncludedEntitlements-columnLabel'>
        {l('Access Status')}
      </Typography>
      <Typography
        kind='body2'
        lineLimit={1}
        tooltipConfigs={{
          text: getAccessStatusText(l, user.accessStatus),
          placement: 'top',
          maxWidth: '10rem',
        }}>
        {getAccessStatusText(l, user.accessStatus)}
      </Typography>
    </Box>
  </div>
);

/**
 * Creates the loading state content for child rows
 */
export const createLoadingStateContent = () => (
  <Box className='Rules-IncludedEntitlements-loadingState'>
    <Typography kind='body2' color='--color-foreground-neutral-subtle'>
      <Loader kind='dots' format='inline' color='brand' />
    </Typography>
  </Box>
);

/**
 * Creates the placeholder content for child rows when no users are loaded
 */
export const createPlaceholderContent = (l: LocalizeFn) => (
  <Box className='Rules-IncludedEntitlements--noUsersFound'>
    <Icon
      kind='AlertInfo'
      size='medium'
      color='--color-foreground-info-medium'
    />
    <Typography kind='body2' color='--color-foreground-neutral-bold'>
      {l("Looks like there aren't any users to display.")}
    </Typography>
  </Box>
);

/**
 * Creates the leading content for parent rows (entitlement groups)
 */
export const createParentLeadingContent = () => (
  <Box className='Rules-IncludedEntitlements-leadingContent'>
    <Icon kind='Entitlement' size='large' />
  </Box>
);
